import { CartCoreModule } from '@wix/cart-core';
import { Box } from '@wix/design-system';
import '@wix/design-system/styles-odeditor.global.css';
import { ActionHandlerMap, DomainSearchGateModule, useIsMobile } from '@wix/domains-search-gate';
import { usePremiumEssentialsContext } from '@wix/premium-fed-sdk/@premium-essentials';
import React, { Suspense, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { HeaderAndFooterProvider } from '../../components/HeaderAndFooterProvider';
import { ProvidersWrapper } from '../../components/ProvidersWrapper';
import { Specs, TSpecsExperiments } from '../../Specs';
import { useAddDomainToCart } from '../../hooks/useAddDomainToCart';
import { navigateToSplitPageStorefront } from '../../utils/storefrontNavigation';
import { isAnonymousCartEligibleDomain } from '../../utils/anonymousCartUtils';
import s from '../wixheader.css';
import {
  navigateToAnonymousCartDomainSplitPage,
  navigateToAnonymousCartViewCart,
} from '../../utils/anonymousCartNavigation';

const basename = window.__BASE_URL__;
const experiments = window.__APP_PARAMS__.experiments
  ? (JSON.parse(window.__APP_PARAMS__.experiments) as TSpecsExperiments)
  : {};
const referralAdditionalInfo =
  new URLSearchParams(window.location.search).get('referralAdditionalInfo') ??
  undefined;

const REFERRAL_ADDITIONAL_INFO = 'storefront_new_gate';
const PAGE_TITLE_KEY = 'domain_search_gate.pageTab.domainPurchaseIntent';

export const AnonymousGatePageLoader = () => {
  return (
    <BrowserRouter basename={basename}>
      <Suspense fallback={<></>}>
        <ProvidersWrapper
          experiments={experiments}
          referralAdditionalInfo={referralAdditionalInfo}
        >
          <AnonymousGatePage />
        </ProvidersWrapper>
      </Suspense>
    </BrowserRouter>
  );
};

const AnonymousGateContent = () => {
  const { experiments: experimentsFromContext } = usePremiumEssentialsContext();
  const { addDomainToCart } = useAddDomainToCart();
  const { t } = DomainSearchGateModule.internals.useTranslations();
  const isMobile = useIsMobile();
  const isAnonymousCartEnabled =
    experimentsFromContext?.[Specs.AnonymousCart] && !isMobile;
  const isStorefrontHeaderEnabled =
    experimentsFromContext?.[Specs.StorefrontResultsPageHeader] && !isMobile;
  const shouldShowCartHeader =
    isAnonymousCartEnabled || isStorefrontHeaderEnabled || !isMobile;

  useEffect(() => {
    document.title = t(PAGE_TITLE_KEY);
  }, [t]);

  const purchaseHandler: ActionHandlerMap['purchase'] = async ({
    domainName,
    isPremiumDomain,
  }) => {
    if (
      isAnonymousCartEnabled &&
      isAnonymousCartEligibleDomain(isPremiumDomain ?? false)
    ) {
      try {
        await addDomainToCart({
          domainName,
        });
        navigateToAnonymousCartDomainSplitPage({
          referralAdditionalInfo: REFERRAL_ADDITIONAL_INFO,
        });
      } catch (e) {
        // TODO: add proper error handling
        console.error('Failed to add domain to cart', e);
      }
      return;
    }

    navigateToSplitPageStorefront({
      domainName,
      referralAdditionalInfo: REFERRAL_ADDITIONAL_INFO,
      isMobileFlow: window.__APP_PARAMS__.device === 'Smartphone',
    });
    // window.open returns synchronously; keep the promise pending so the button
    // loader stays visible until the browser actually unloads the page
    await new Promise<never>(() => {});
  };

  return (
    <DomainSearchGateModule.SDK.Pages.AnonymousGatePages
      handlers={{
        purchaseRootDomainIsRequired: purchaseHandler,
        purchase: purchaseHandler,
      }}
      showCartHeader={shouldShowCartHeader}
    />
  );
};

const AnonymousGatePage = () => {
  const { i18n, experiments: experimentsFromContext } =
    usePremiumEssentialsContext();
  const isMobile = useIsMobile();
  const isAnonymousCartEnabled =
    experimentsFromContext?.[Specs.AnonymousCart] && !isMobile;
  const isStorefrontHeaderEnabled =
    experimentsFromContext?.[Specs.StorefrontResultsPageHeader] && !isMobile;
  const shouldShowCartHeader =
    isAnonymousCartEnabled || isStorefrontHeaderEnabled || !isMobile;

  useEffect(() => {
    const pageUrl = window.location.href;

    HeaderAndFooterProvider({
      language: i18n.language ?? 'en',
      brand: 'wix',
      footer: true,
      stickyBehaviour: 'sticky',
      postLogin: pageUrl,
      postSignUp: pageUrl,
      postSignOut: pageUrl,
      header: true,
    });
  }, [i18n.language]);

  return (
    <Box
      display='grid'
      gridTemplateRows='auto 1fr auto'
      gridTemplateColumns='1fr'
      boxSizing='border-box'
      width='100%'
      minHeight='100dvh'
    >
      {shouldShowCartHeader && (
        <Box position='sticky' top='0' zIndex={15}>
          <CartCoreModule.Provider
            translations={{ Loading: <></> }}
            cartCoreApiClientExtension={{
              allowAnonymous: Boolean(isAnonymousCartEnabled),
              Loading: <></>,
            }}
          >
            <CartCoreModule.SDK.Components.PageHeader
              showTrustBuilder={false}
              miniCartButtonProps={
                isAnonymousCartEnabled
                  ? {
                      onViewCart: () => {
                        navigateToAnonymousCartViewCart({
                          referralAdditionalInfo: REFERRAL_ADDITIONAL_INFO,
                        });
                      },
                    }
                  : { hidden: true }
              }
            />
            <CartCoreModule.SDK.Modals />
          </CartCoreModule.Provider>
        </Box>
      )}
      <section
        id='header'
        style={shouldShowCartHeader ? { display: 'none' } : undefined}
        className={s.header}
      />
      <Box minHeight='100dvh'>
        <DomainSearchGateModule.Provider translations={{ Loading: <></> }}>
          <AnonymousGateContent />
        </DomainSearchGateModule.Provider>
      </Box>
      <section id='footer' />
      {isStorefrontHeaderEnabled && (
        <Box height='40px'>
          <CartCoreModule.Provider translations={{ Loading: <></> }}>
            <CartCoreModule.SDK.Components.BottomBanner />
          </CartCoreModule.Provider>
        </Box>
      )}
    </Box>
  );
};
